import { useContext, useEffect, useState } from "react"
import BaseModal from "../../../../components/layout/BaseModal"
import BillModalScreen1 from "./AddBillModalScreen1"
import AddBillOrOfferModalScreen2 from "./AddBillOrOfferModalScreen2"
import AddBillOrOfferModalScreen3 from "./AddBillOrOfferModalScreen3"
import AddBillOrOfferModalScreen4 from "./AddBillOrOfferModalScreen4"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Loading from "../../../../components/common/Loading"
import ErrorPage from "../../../404/ErrorPage"
import { getCustomerNamesOnly, getSingleCustomerData } from "../../../Kunden/api/apiCalls"
import { ModalContext } from "../../../../components/contexts/ModalContext"
import { BillPost } from "../../../../data/Types"
import { useAddBillMutation } from "../../api/services"
import { useDelayWhenSearchWithPagination } from "../../../../services/functions"
import { Form, FormInstance } from "antd"
import { useLocation } from "react-router-dom"
import { BillCategorie, BillTypes, GeneralQueryKeys } from "../../../../api/constants"
import { getMajorOrderNamesOnly, getSingleMajorOrderData } from "../../api/apiCalls"
import { P } from "../../../../auth/permissions"
import { Authorized } from "../../../../components/authentication/Authorized"

type AddBillModalType = {
    queryKey: string
}

const startDataForAddBill = {
    date: new Date(),
    billType: BillTypes.RECHNUNG,
    billCategorie: BillCategorie.HAUSMEISTER,
    billDay: 28,
    billDue: 28,
    manualReminderEnabled: false,
}

export default function AddBillModal(props: AddBillModalType) {
    const [newBill, setnewBill] = useState<BillPost>(startDataForAddBill as BillPost)

    // GET all major orders for search
    const [searchTermMajorOrder, setsearchTermMajorOrder] = useState("")
    const searchParamsMajorOrder = useDelayWhenSearchWithPagination(searchTermMajorOrder)
    const queryKeyAllMajorOrder = GeneralQueryKeys.MAJOR_ORDER_ALL + searchParamsMajorOrder
    const {
        data: majorOrderData,
        error: errorMajorOrder,
        isLoading: isLoadingMajorOrder,
    } = useQuery({
        queryKey: [queryKeyAllMajorOrder],
        queryFn: () => getMajorOrderNamesOnly(searchParamsMajorOrder),
    })

    // GET only the selected major orders customer_id / object_id
    const queryKeyMajorOrderSingle = GeneralQueryKeys.MAJOR_ORDER_SINGLE + newBill.major_order_id
    const { data: majorOrderSingle } = useQuery({
        queryKey: [queryKeyMajorOrderSingle],
        queryFn: () => getSingleMajorOrderData({ id: newBill.major_order_id ?? "", minimal: "customer_id,object_id" }),
        enabled: !!newBill.major_order_id,
    })

    // GET all customer names for search
    const [searchTerm, setsearchTerm] = useState("")
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const queryKeyAllCustomer = GeneralQueryKeys.CUSTOMER_NAMES_ONLY + searchParams
    const {
        data: customerData,
        error: errorCustomer,
        isLoading: isLoadingCustomer,
    } = useQuery({
        queryKey: [queryKeyAllCustomer],
        queryFn: () => getCustomerNamesOnly(searchParams),
    })

    // GET single customer
    const queryKeySingleCustomer = GeneralQueryKeys.CUSTOMER_SINGLE + newBill?.customer_id
    const {
        data: singleCustomer,
        error: errorSingleCustomer,
        isLoading: isLoadingSingleCustomer,
    } = useQuery({
        queryKey: [queryKeySingleCustomer],
        queryFn: () =>
            getSingleCustomerData(
                newBill?.customer_id ?? "",
                "customer.name",
                "-nr,-objectInfo,-customer_id,-employee_ids,-mailReports,-s3Documents,-serviceContract,-archived,-ranking,-deleted,-costCenter,-zipExportDownloadedBy"
            ),
        enabled: !!newBill?.customer_id,
    })

    const [documents, setdocuments] = useState<
        {
            title: ""
            fileUpload: File
            uploadedByUser: true
        }[]
    >()

    // all about creating the new bill
    const queryClient = useQueryClient()
    const modalCon = useContext(ModalContext)
    const { mutate: addBill } = useAddBillMutation(queryClient, props.queryKey)
    const handleNewBill = () => {
        const formData = new FormData()
        if (!(newBill.billType === BillTypes.DAUER_RECHNUNG || newBill.billType === BillTypes.JAHRES_RECHNUNG)) {
            setnewBill((prev) => ({
                ...prev,
                billDay: undefined,
            }))
        }
        console.log(newBill)
        formData.append("bill", JSON.stringify(newBill))
        documents?.forEach((doc: any) => {
            if (doc) {
                formData.append("additionalDocuments", doc)
            }
        })
        addBill(formData)
        clearFormAndModal()
    }

    // FE LOGIC
    const [screen, setscreen] = useState<number>(1)
    const [formScreen1] = Form.useForm()
    const [formScreen2] = Form.useForm()
    const clearFormAndModal = () => {
        formScreen1.resetFields()
        formScreen1.setFieldsValue(startDataForAddBill)
        setnewBill(startDataForAddBill as BillPost)
        setdocuments(undefined)
        formScreen2.resetFields()
        setscreen(1)
        modalCon?.trigger(0)
    }
    const handleNextModalScreen = async (screen: number, form?: FormInstance) => {
        try {
            if (form) await form.validateFields()
            setscreen(screen)
        } catch (e) {
            console.log(e)
        }
    }

    // IN CASE OBJECT-ID / CUSTOMER-ID HAVE BEEN PASSED THROUGH THE URL
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    useEffect(() => {
        formScreen1.setFieldsValue(startDataForAddBill)

        if (queryParams.get("mid") && !newBill?.major_order_id) {
            const mid = queryParams.get("mid")
            queryParams.delete("mid")
            setnewBill((prev: any) => ({
                ...prev,
                major_order_id: mid,
                billType: BillTypes.ABSCHLAGS_RECHNUNG,
            }))
            formScreen1.setFieldsValue({
                major_order_id: mid,
                billType: BillTypes.ABSCHLAGS_RECHNUNG,
            })
        }
        if (queryParams.get("oid") && !newBill?.object_id) {
            const oid = queryParams.get("oid")
            const adress = queryParams.get("adress")
            queryParams.delete("oid")
            queryParams.delete("adress")
            setnewBill((prev: any) => ({
                ...prev,
                object_id: oid,
            }))
            formScreen1.setFieldsValue({ object_id: adress })
        }
        if (queryParams.get("cid") && !newBill?.customer_id) {
            const cid = queryParams.get("cid")
            const customer = queryParams.get("customer")
            queryParams.delete("cid")
            queryParams.delete("customer")
            setnewBill((prev: any) => ({
                ...prev,
                customer_id: cid,
            }))
            formScreen1.setFieldsValue({ customer_id: customer })
        }
    }, [])

    // MAJOR ORDER FE LOGIC
    useEffect(() => {
        if (majorOrderSingle)
            setnewBill((prev: BillPost) => ({
                ...prev,
                customer_id: majorOrderSingle?.customer_id?._id,
                object_id: majorOrderSingle?.object_id?._id,
            }))
    }, [majorOrderSingle])

    return (
        <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
            <BaseModal
                title={"Neue Rechnung erstellen"}
                modalId={1}
                bottomRow={
                    <div className="flex justify-between items-center">
                        <button
                            disabled={screen === 1}
                            className={`underline text-sm ${screen === 1 && "cursor-not-allowed text-gray-300"}`}
                            onClick={() => setscreen(screen - 1)}>
                            zurück
                        </button>
                        {screen === 4 ? (
                            <button className={`btn btn-primary`} onClick={handleNewBill}>
                                Abschicken
                            </button>
                        ) : screen === 3 ? (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(4)}>
                                Weiter
                            </button>
                        ) : screen === 2 ? (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(3, formScreen2)}>
                                Weiter
                            </button>
                        ) : (
                            <button className="btn btn-primary btn-outline" onClick={() => handleNextModalScreen(2, formScreen1)}>
                                Weiter
                            </button>
                        )}
                    </div>
                }>
                <div className="overflow-hidden relative h-full w-full">
                    {isLoadingSingleCustomer && <Loading />}
                    {errorSingleCustomer && <ErrorPage />}
                    {!errorSingleCustomer && (
                        <>
                            <BillModalScreen1
                                visible={screen >= 1}
                                form={formScreen1}
                                newBill={newBill as BillPost}
                                setnewBill={setnewBill}
                                objectArray={singleCustomer?.objects}
                                customerArray={customerData}
                                setsearchTerm={setsearchTerm}
                                isLoadingCustomer={isLoadingCustomer}
                                errorCustomer={errorCustomer}
                                majorOrderArray={majorOrderData}
                                setsearchTermMajorOrder={setsearchTermMajorOrder}
                                isLoadingMajorOrder={isLoadingMajorOrder}
                                errorMajorOrder={errorMajorOrder}
                            />
                            <AddBillOrOfferModalScreen2
                                form={formScreen2}
                                visible={screen >= 2}
                                newBillOrOffer={newBill as BillPost}
                                setnewBillOrOffer={setnewBill}
                            />
                            <AddBillOrOfferModalScreen3 visible={screen >= 3} documents={documents ?? []} setdocuments={setdocuments} />
                            <AddBillOrOfferModalScreen4
                                visible={screen >= 4}
                                newBillOrOffer={newBill as BillPost}
                                customer={singleCustomer}
                                documents={documents}
                            />
                        </>
                    )}
                </div>
            </BaseModal>
        </Authorized>
    )
}
